import React, { useState, useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { Modal, Button, Input, Tag, Tooltip, Spin, Table, notification, message, Timeline } from "antd";
import {
  NotificationOutlined, SearchOutlined,
  ArrowLeftOutlined, ArrowRightOutlined, SendOutlined,
  PlusOutlined, CloseOutlined, ThunderboltOutlined, WhatsAppOutlined,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { SEND_TEMPLATE_API, SEND_IMAGE_API, SEND_TEXT_API } from "./constants";
import {
  _fetchCredits, _getTemplateHeaderSrc, _previewTemplateBody, substituteQRText,
} from "./helpers";
import { _resolveMediaUrl } from "./MessageParts";

// ── Template Components ───────────────────────────────────────────────────────
function TemplateCard({ template, onSelect }) {
  const [imgUrl, setImgUrl] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const src = _getTemplateHeaderSrc(template);
    if (!src) return;
    const el = cardRef.current;
    if (!el) return;
    const isExternal = src.startsWith("http");
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (isExternal) setImgUrl(src);
        else _resolveMediaUrl(src).then(url => setImgUrl(url));
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [template.id]); // eslint-disable-line

  const previewBody = (template.body || "").replace(/\{\{\d+\}\}/g, m => {
    const idx = parseInt(m.replace(/[{}]/g, "")) - 1;
    const param = template.customParams?.[idx];
    return param ? `[${param.paramName}]` : m;
  });

  return (
    <div
      ref={cardRef}
      onClick={onSelect}
      style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 6, cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#1677ff"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(22,119,255,0.12)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.boxShadow = ""; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{template.elementName.replace(/_/g, " ")}</span>
        <Tag color="blue" style={{ fontSize: 9, margin: 0, flexShrink: 0 }}>{template.category}</Tag>
      </div>
      {imgUrl && <img src={imgUrl} alt="" style={{ width: "100%", maxHeight: 80, objectFit: "cover", borderRadius: 6 }} />}
      <div style={{ fontSize: 11, color: "#595959", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", whiteSpace: "pre-wrap" }}>
        {previewBody || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No body</span>}
      </div>
      {template.customParams?.length > 0 && (
        <span style={{ fontSize: 10, color: "#8c8c8c" }}>{template.customParams.length} variable{template.customParams.length > 1 ? "s" : ""}</span>
      )}
      <Button size="small" type="primary" ghost style={{ alignSelf: "flex-end", fontSize: 11 }} onClick={e => { e.stopPropagation(); onSelect(); }}>Use ↵</Button>
    </div>
  );
}

export function TemplatesModal({ open, onClose, templates, loading, onSelect }) {
  const [search, setSearch] = useState("");
  const [credit, setCredit] = useState(null);
  useEffect(() => { if (open) _fetchCredits().then(c => setCredit(c)); }, [open]);
  const filtered = templates.filter(t => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return t.elementName.toLowerCase().includes(s) || (t.body || "").toLowerCase().includes(s);
  });
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ textAlign: "center", fontSize: 12, color: "#8c8c8c" }}>
          Balance:&nbsp;
          {credit !== null
            ? <span style={{ fontWeight: 700, color: "#1677ff" }}>${credit.toFixed(2)}</span>
            : <span style={{ color: "#bfbfbf" }}>—</span>}
        </div>
      }
      title={<span><NotificationOutlined style={{ color: "#1677ff", marginRight: 6 }} />Template Messages</span>}
      width={960}
      style={{ top: 16 }}
      styles={{ body: { padding: "12px 16px", height: "calc(100vh - 172px)", display: "flex", flexDirection: "column", overflow: "hidden" } }}
      destroyOnClose
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, padding: "10px 12px", minHeight: 0 }}>
        <Input
          placeholder="Search templates…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
          style={{ marginBottom: 12, flexShrink: 0 }}
        />
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {loading
            ? <div style={{ textAlign: "center", padding: "32px 0" }}><Spin /></div>
            : filtered.length === 0
            ? <div style={{ textAlign: "center", color: "#bfbfbf", padding: "32px 0", fontSize: 13 }}>No templates found</div>
            : <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {filtered.map(t => <TemplateCard key={t.id} template={t} onSelect={() => onSelect(t)} />)}
              </div>
          }
        </div>
      </div>
    </Modal>
  );
}

// ── Bulk Template Send ────────────────────────────────────────────────────────
export function BulkTemplatePreviewModal({ open, onClose, template, authState, onNext, zIndex }) {
  const [recipients, setRecipients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [credit, setCredit] = useState(null);

  useEffect(() => {
    if (!template) return;
    const blank = (template.customParams || []).map(() => "");
    const first = { id: uuidv4(), phone: "", params: blank };
    setRecipients([first]);
    setSelectedId(first.id);
    setImgUrl(null);
    const src = _getTemplateHeaderSrc(template);
    if (src) {
      if (src.startsWith("http")) setImgUrl(src);
      else _resolveMediaUrl(src).then(u => setImgUrl(u));
    }
  }, [template?.id]); // eslint-disable-line

  useEffect(() => { if (open) _fetchCredits().then(c => setCredit(c)); }, [open]);

  const selected = recipients.find(r => r.id === selectedId) ?? recipients[0] ?? null;

  const addRecipient = () => {
    const blank = (template?.customParams || []).map(() => "");
    const r = { id: uuidv4(), phone: "", params: blank };
    setRecipients(prev => [...prev, r]);
    setSelectedId(r.id);
  };

  const removeRecipient = (id) => {
    setRecipients(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter(r => r.id !== id);
      return next;
    });
    setSelectedId(prev => (prev === id ? null : prev));
  };

  const updatePhone = (id, phone) => setRecipients(prev => prev.map(r => r.id === id ? { ...r, phone } : r));
  const updateParam = (idx, val) => setRecipients(prev => prev.map(r => {
    if (r.id !== selectedId) return r;
    const p = [...r.params]; p[idx] = val; return { ...r, params: p };
  }));

  if (!template) return null;

  const previewBody = (selected && template) ? _previewTemplateBody(template, selected.params) : "";
  const hasParams = (template.customParams || []).length > 0;
  const validRecipients = recipients.filter(r => r.phone.trim().length >= 5);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      zIndex={zIndex}
      title={<span><NotificationOutlined style={{ color: "#1677ff", marginRight: 6 }} />{template.elementName.replace(/_/g, " ")} · Bulk Send</span>}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onClose}>Back</Button>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            Balance:&nbsp;
            {credit !== null
              ? <span style={{ fontWeight: 700, color: "#1677ff" }}>${credit.toFixed(2)}</span>
              : <span style={{ color: "#bfbfbf" }}>—</span>}
          </div>
          <Button type="primary" icon={<ArrowRightOutlined />} disabled={validRecipients.length === 0}
            onClick={() => onNext(validRecipients)}>
            Next ({validRecipients.length})
          </Button>
        </div>
      }
      width={1100}
      style={{ top: 16 }}
      styles={{ body: { padding: "12px 16px", height: "calc(100vh - 172px)", display: "flex", overflow: "hidden", gap: 12 } }}
      destroyOnClose
    >
      {/* Col 1 — Recipients */}
      <div style={{ width: 240, flexShrink: 0, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#595959", background: "#fafafa", borderBottom: "1px solid #f0f0f0", flexShrink: 0, letterSpacing: 0.3 }}>
          RECIPIENTS&nbsp;<span style={{ fontWeight: 400, color: "#8c8c8c" }}>({validRecipients.length}/{recipients.length})</span>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {recipients.map((r, idx) => (
            <div key={r.id} onClick={() => setSelectedId(r.id)}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 7px", borderRadius: 7, cursor: "pointer", border: `1.5px solid ${r.id === selectedId ? "#1677ff" : "#e0e0e0"}`, background: r.id === selectedId ? "#e6f4ff" : "#fff", transition: "all 0.12s" }}>
              <span style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0, width: 16 }}>#{idx + 1}</span>
              <Input size="small" value={r.phone}
                onChange={e => { e.stopPropagation(); updatePhone(r.id, e.target.value); }}
                onClick={e => e.stopPropagation()}
                placeholder="Phone number…"
                style={{ flex: 1, fontSize: 12 }} />
              {recipients.length > 1 && (
                <Tooltip title="Remove">
                  <CloseOutlined style={{ fontSize: 10, color: "#ff4d4f", flexShrink: 0, cursor: "pointer" }}
                    onClick={e => { e.stopPropagation(); removeRecipient(r.id); }} />
                </Tooltip>
              )}
            </div>
          ))}
        </div>
        <div style={{ padding: "8px 10px", borderTop: "1px solid #f0f0f0", flexShrink: 0 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={addRecipient} block style={{ borderStyle: "dashed", fontSize: 12 }}>
            Add recipient
          </Button>
        </div>
      </div>

      {/* Col 2 — Params for selected recipient */}
      <div style={{ width: 210, flexShrink: 0, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#595959", background: "#fafafa", borderBottom: "1px solid #f0f0f0", flexShrink: 0, letterSpacing: 0.3 }}>
          {selected ? `VARIABLES · #${recipients.indexOf(selected) + 1}` : "VARIABLES"}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {!hasParams
            ? <div style={{ fontSize: 12, color: "#bfbfbf", fontStyle: "italic" }}>No variables</div>
            : (template.customParams || []).map((param, i) => (
              <div key={param.paramName} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 500 }}>{param.paramName}</label>
                <Input size="small" value={selected?.params?.[i] || ""}
                  onChange={e => updateParam(i, e.target.value)}
                  placeholder={`Enter ${param.paramName}…`} />
              </div>
            ))
          }
        </div>
      </div>

      {/* Col 3 — Preview */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#8c8c8c", background: "#fafafa", borderBottom: "1px solid #f0f0f0", flexShrink: 0, letterSpacing: 0.3 }}>
          CUSTOMER'S VIEW
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12, background: "#efeae2" }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ maxWidth: "90%", background: "#d9fdd3", borderRadius: "12px 4px 12px 12px", overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
              {imgUrl && <img src={imgUrl} alt="" style={{ width: "100%", display: "block", maxHeight: 220, objectFit: "cover" }} />}
              <div style={{ padding: "6px 10px 2px" }}>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#111", wordBreak: "break-word" }}>
                  {previewBody || <span style={{ color: "#999", fontStyle: "italic" }}>Fill variables to preview</span>}
                </div>
                {template.footer && <div style={{ fontSize: 11, color: "#667781", marginTop: 3 }}>{template.footer}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2, marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "#667781" }}>now ✓✓</span>
                </div>
              </div>
              {(template.buttons || []).map((btn, i) => (
                <React.Fragment key={i}>
                  <div style={{ height: 1, background: "rgba(0,0,0,0.1)" }} />
                  <div style={{ padding: "8px 10px", textAlign: "center", color: "#009de2", fontSize: 13, fontWeight: 500 }}>
                    {btn.parameter?.text}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function BulkSummaryModal({ open, onClose, onDone, afterClose, template, recipients, authState, zIndex }) {
  const [statuses, setStatuses] = useState({});
  const [started, setStarted] = useState(false);
  const [allDone, setAllDone] = useState(false);

  useEffect(() => {
    if (!open) { setStatuses({}); setStarted(false); setAllDone(false); }
  }, [open]);

  const setStatus = (id, state, error = null) =>
    setStatuses(prev => ({ ...prev, [id]: { state, error } }));

  const handleSendAll = async () => {
    if (started) return;
    setStarted(true);
    for (const r of recipients) {
      setStatus(r.id, "sending");
      try {
        const parameters = (template.customParams || []).map((p, i) => ({ name: p.paramName, value: r.params[i] || "" }));
        await axios.post(SEND_TEMPLATE_API, {
          phone: r.phone.trim(),
          localMessageId: uuidv4(),
          temp_name: template.elementName,
          parameters,
          userId: authState.id,
        }, { headers: { accessToken: localStorage.getItem("accessToken") } });
        setStatus(r.id, "sent");
      } catch (e) {
        setStatus(r.id, "failed", e?.response?.data?.message || "Failed");
      }
    }
    setAllDone(true);
  };

  useEffect(() => {
    if (!allDone) return;
    const anyFailed = recipients.some(r => statuses[r.id]?.state === "failed");
    if (!anyFailed) {
      const t = setTimeout(() => onDone(), 2000);
      return () => clearTimeout(t);
    }
  }, [allDone]); // eslint-disable-line

  const statusOf = id => statuses[id] ?? { state: "pending" };

  const statusChip = (id) => {
    const { state, error } = statusOf(id);
    if (state === "pending") return <span style={{ fontSize: 11, color: "#8c8c8c" }}>—</span>;
    if (state === "sending") return <span style={{ fontSize: 11, color: "#1677ff", display: "inline-flex", alignItems: "center", gap: 4 }}><Spin size="small" />Sending…</span>;
    if (state === "sent") return <span style={{ fontSize: 11, color: "#52c41a", fontWeight: 600 }}>✅ Sent</span>;
    return (
      <Tooltip title={error}>
        <span style={{ fontSize: 11, color: "#ff4d4f", fontWeight: 600, cursor: "default" }}>❌ Failed</span>
      </Tooltip>
    );
  };

  if (!template || !recipients?.length) return null;

  const anyFailed = recipients.some(r => statuses[r.id]?.state === "failed");

  return (
    <Modal
      open={open}
      onCancel={!started || allDone ? onClose : undefined}
      closable={!started || allDone}
      zIndex={zIndex}
      title={<span><NotificationOutlined style={{ color: "#1677ff", marginRight: 6 }} />Bulk Send Summary</span>}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {!started && (
              <Button icon={<ArrowLeftOutlined />} onClick={onClose}>Back</Button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {allDone && anyFailed && (
              <Button onClick={onClose}>Close</Button>
            )}
            {allDone && !anyFailed && (
              <span style={{ fontSize: 12, color: "#52c41a", fontWeight: 600 }}>All sent — closing…</span>
            )}
            {!started && (
              <Button type="primary" icon={<SendOutlined />} onClick={handleSendAll}>
                Send All ({recipients.length})
              </Button>
            )}
          </div>
        </div>
      }
      width={680}
      style={{ top: 16 }}
      styles={{ body: { padding: "12px 16px", maxHeight: "calc(100vh - 172px)", overflowY: "auto" } }}
      afterClose={afterClose}
      destroyOnClose
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 100px", gap: 8, padding: "6px 10px", background: "#fafafa", borderRadius: "8px 8px 0 0", border: "1px solid #f0f0f0", fontSize: 11, fontWeight: 600, color: "#8c8c8c" }}>
          <span>PHONE</span><span>PREVIEW</span><span style={{ textAlign: "right" }}>STATUS</span>
        </div>
        {recipients.map((r, idx) => {
          const resolvedText = _previewTemplateBody(template, r.params);
          return (
            <div key={r.id} style={{ display: "grid", gridTemplateColumns: "160px 1fr 100px", gap: 8, padding: "8px 10px", borderLeft: "1px solid #f0f0f0", borderRight: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0", background: idx % 2 === 0 ? "#fff" : "#fafafa", alignItems: "center", borderRadius: idx === recipients.length - 1 ? "0 0 8px 8px" : undefined }}>
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace" }}>{r.phone}</span>
              <span style={{ fontSize: 11, color: "#595959", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {resolvedText || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No body</span>}
              </span>
              <div style={{ textAlign: "right" }}>{statusChip(r.id)}</div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

export function TemplatePreviewModal({ open, onClose, template, chat, authState, onSent, onOptimisticFail }) {
  const [paramValues, setParamValues] = useState([]);
  const [imgUrl, setImgUrl]           = useState(null);
  const [sending, setSending]         = useState(false);
  const [credit, setCredit]           = useState(null);
  const creditRef                     = useRef(null);

  useEffect(() => { if (open) _fetchCredits().then(c => { setCredit(c); creditRef.current = c; }); }, [open]);

  useEffect(() => {
    if (!template) return;
    const firstName = (authState?.user?.name || authState?.username || "").split(" ")[0];
    setParamValues((template.customParams || []).map(p => {
      const pn = (p.paramName || "").toLowerCase().replace(/[\s.]/g, "");
      if (pn === "name") return chat?.customer_name || "";
      if (pn === "operatorfirstname" || pn === "operatorfirst_name") return firstName;
      return "";
    }));
    setImgUrl(null);
    const src = _getTemplateHeaderSrc(template);
    if (src) {
      if (src.startsWith("http")) setImgUrl(src);
      else _resolveMediaUrl(src).then(url => setImgUrl(url));
    }
  }, [template?.id]); // eslint-disable-line

  if (!template) return null;

  const previewBody = _previewTemplateBody(template, paramValues);
  const hasParams   = (template.customParams || []).length > 0;

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    const localMessageId = uuidv4();
    const tempId = `temp_${Date.now()}`;
    const parameters = (template.customParams || []).map((p, i) => ({
      name: p.paramName,
      value: paramValues[i] || "",
    }));
    onSent?.({ localMessageId, tempId, text: previewBody, headerUrl: imgUrl });
    try {
      await axios.post(SEND_TEMPLATE_API, {
        phone: chat.phone,
        localMessageId,
        temp_name: template.elementName,
        parameters,
        userId: authState.id,
      }, { headers: { accessToken: localStorage.getItem("accessToken") } });
      const before = creditRef.current;
      const after = await _fetchCredits();
      if (before !== null && after !== null) {
        const spent = Math.round((before - after) * 100000) / 100000;
        if (spent > 0) {
          notification.info({
            message: `$${parseFloat(spent.toFixed(5))} was spent`,
            placement: "top",
            duration: 5,
          });
        }
      }
    } catch {
      onOptimisticFail?.({ tempId, localMessageId });
      message.error("Failed to send template.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span><NotificationOutlined style={{ color: "#1677ff", marginRight: 6 }} />{template.elementName.replace(/_/g, " ")}</span>}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onClose} disabled={sending}>Back</Button>
          <div style={{ textAlign: "center", fontSize: 12, color: "#8c8c8c" }}>
            Balance:&nbsp;
            {credit !== null
              ? <span style={{ fontWeight: 700, color: "#1677ff" }}>${credit.toFixed(2)}</span>
              : <span style={{ color: "#bfbfbf" }}>—</span>}
          </div>
          <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={handleSend}>Send</Button>
        </div>
      }
      width={780}
      style={{ top: 16 }}
      styles={{ body: { padding: "12px 16px", height: "calc(100vh - 172px)", display: "flex", overflow: "hidden" } }}
      destroyOnClose
    >
      {/* Left — parameters */}
      <div style={{ width: 220, flexShrink: 0, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 10, padding: "12px 14px", marginRight: 12, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#595959", flexShrink: 0 }}>Variables</div>
        {hasParams ? template.customParams.map((param, i) => (
          <div key={param.paramName} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 500 }}>{param.paramName}</label>
            <Input
              value={paramValues[i] || ""}
              onChange={e => setParamValues(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
              placeholder={`Enter ${param.paramName}…`}
              size="small"
            />
          </div>
        )) : (
          <div style={{ fontSize: 12, color: "#bfbfbf", fontStyle: "italic" }}>No variables</div>
        )}
      </div>

      {/* Right — bubble preview */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#8c8c8c", background: "#fafafa", borderBottom: "1px solid #f0f0f0", flexShrink: 0, letterSpacing: 0.3 }}>
          CUSTOMER'S VIEW
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "12px", background: "#efeae2" }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ maxWidth: "90%", background: "#d9fdd3", borderRadius: "12px 4px 12px 12px", overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
              {imgUrl && <img src={imgUrl} alt="" style={{ width: "100%", display: "block", maxHeight: 220, objectFit: "cover" }} />}
              <div style={{ padding: "6px 10px 2px" }}>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#111", wordBreak: "break-word" }}>
                  {previewBody}
                </div>
                {template.footer && (
                  <div style={{ fontSize: 11, color: "#667781", marginTop: 3 }}>{template.footer}</div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2, marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "#667781" }}>now ✓✓</span>
                </div>
              </div>
              {template.buttons?.length > 0 && template.buttons.map((btn, i) => (
                <React.Fragment key={i}>
                  <div style={{ height: 1, background: "rgba(0,0,0,0.1)" }} />
                  <div style={{ padding: "8px 10px", textAlign: "center", color: "#009de2", fontSize: 13, fontWeight: 500 }}>
                    {btn.type === "url" ? "🔗 " : "↩ "}{btn.parameter?.text}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Quick Reply Components ────────────────────────────────────────────────────
function QuickReplyCard({ qr, authState, onSelect }) {
  const [imageUrl, setImageUrl] = useState(null);
  const cardRef = useRef(null);
  useEffect(() => {
    if (qr.type !== "image" || !qr.media?.data) return;
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        _resolveMediaUrl(qr.media.data).then(url => setImageUrl(url));
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [qr.id]); // eslint-disable-line
  const previewText = substituteQRText(qr.text || qr.media?.caption || "", authState);
  return (
    <div
      ref={cardRef}
      onClick={onSelect}
      style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 6, cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s", minWidth: 0, overflow: "hidden" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#1677ff"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(22,119,255,0.12)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.boxShadow = ""; }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{qr.name}</span>
        <span style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0 }}>⚡ {(qr.triggered || 0).toLocaleString()}</span>
      </div>
      {imageUrl && <img src={imageUrl} alt="" style={{ width: "100%", maxHeight: 80, objectFit: "cover", borderRadius: 6 }} />}
      <div style={{ fontSize: 11, color: "#595959", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
        {previewText || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No text</span>}
      </div>
      <Button size="small" type="primary" ghost style={{ alignSelf: "flex-end", marginTop: 2, fontSize: 11 }} onClick={e => { e.stopPropagation(); onSelect(); }}>
        Use ↵
      </Button>
    </div>
  );
}

export function QuickRepliesModal({ open, onClose, quickReplies, loading, onSelect, authState }) {
  const [search, setSearch] = useState("");
  const filtered = quickReplies.filter(qr => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (qr.name || "").toLowerCase().includes(s) || (qr.text || "").toLowerCase().includes(s);
  });
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={<span><ThunderboltOutlined style={{ color: "#faad14", marginRight: 6 }} />Quick Replies</span>}
      width={1080}
      style={{ top: 16 }}
      styles={{ body: { padding: "12px 16px", height: "calc(100vh - 130px)", display: "flex", flexDirection: "column", overflow: "hidden" } }}
      destroyOnClose
    >
      <div className="border rounded p-2" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Input
          placeholder="Search by name or text…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
          style={{ marginBottom: 12, flexShrink: 0 }}
        />
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {loading
            ? <div style={{ textAlign: "center", padding: "32px 0" }}><Spin /></div>
            : filtered.length === 0
              ? <div style={{ textAlign: "center", color: "#bfbfbf", padding: "32px 0", fontSize: 13 }}>No quick replies found</div>
              : <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {filtered.map(qr => <QuickReplyCard key={qr.id} qr={qr} authState={authState} onSelect={() => onSelect(qr)} />)}
                </div>
          }
        </div>
      </div>
    </Modal>
  );
}

export function QuickReplyPreviewModal({ open, onClose, item, authState, chat, ensureChatOpen, onSent }) {
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [paramValues, setParamValues] = useState({});

  useEffect(() => {
    if (!item) return;
    const rawText = substituteQRText(item.text || item.media?.caption || "", authState);
    setText(rawText);
    const assignedFirstName = (chat?.agent || "").split(" ")[0];
    const rawParams = [...new Set((rawText.match(/\{\{([^}]+)\}\}/g) || []).map(m => m.slice(2, -2).trim()))];
    const defaults = {};
    rawParams.forEach(param => {
      const pn = param.toLowerCase().replace(/[\s._]/g, "");
      if (pn === "name") defaults[param] = chat?.customer_name || "";
      else if (pn === "operatorfirstname") defaults[param] = assignedFirstName;
    });
    setParamValues(defaults);
    setImageUrl(null);
    if (item.type === "image" && item.media?.data) {
      _resolveMediaUrl(item.media.data).then(url => setImageUrl(url));
    }
  }, [item?.id]); // eslint-disable-line

  const detectedParams = useMemo(() => {
    const raw = [...(text.match(/\{\{([^}]+)\}\}/g) || [])].map(m => m.slice(2, -2).trim());
    return [...new Set(raw)];
  }, [text]);

  const previewText = useMemo(() => {
    let t = text;
    detectedParams.forEach(param => {
      const val = paramValues[param];
      if (val) t = t.replace(new RegExp(`\\{\\{${param.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\}\\}`, "g"), val);
    });
    return t;
  }, [text, paramValues, detectedParams]);

  const handleSend = async () => {
    if (!item || sending) return;
    setSending(true);
    try {
      await ensureChatOpen();
      const localMessageId = uuidv4();
      const head = { headers: { accessToken: localStorage.getItem("accessToken") } };
      if (item.type === "image") {
        await axios.post(SEND_IMAGE_API, {
          whatsappId: chat.phone, b64Image: item.media.data, serverFile: true,
          localMessageId, userId: authState.id,
          mimeType: item.media?.mimeType || "image/jpeg",
          caption: previewText, ticket_status: chat?.ticket_status ?? null,
        }, head);
      } else {
        await axios.post(SEND_TEXT_API, {
          whatsappId: chat.phone, text: previewText, localMessageId,
          userId: authState.id, ticket_status: chat?.ticket_status ?? null,
        }, head);
      }
      onSent?.();
    } catch {
      message.error("Failed to send quick reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span><ThunderboltOutlined style={{ color: "#faad14", marginRight: 6 }} />{item?.name}</span>}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onClose} disabled={sending}>Back</Button>
          <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={handleSend}>Send</Button>
        </div>
      }
      width={780}
      style={{ top: 16 }}
      styles={{ body: { padding: "12px 16px", height: "calc(100vh - 172px)", display: "flex", overflow: "hidden" } }}
      destroyOnClose
    >
      {/* Left — editable text + param editors */}
      <div style={{ width: 340, flexShrink: 0, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, padding: "12px 14px", marginRight: 12, overflow: "hidden", gap: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#595959", marginBottom: 10, flexShrink: 0 }}>Message</div>
        {imageUrl && <img src={imageUrl} alt="" style={{ width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 7, marginBottom: 10, flexShrink: 0 }} />}
        <Input.TextArea
          value={text}
          onChange={e => setText(e.target.value)}
          style={{ flex: detectedParams.length > 0 ? "0 0 auto" : 1, minHeight: detectedParams.length > 0 ? 80 : undefined, fontSize: 13, resize: "none", border: "none", padding: 0, boxShadow: "none", background: "transparent" }}
          placeholder="Message text…"
        />
        {detectedParams.length > 0 && (
          <div style={{ flex: 1, overflowY: "auto", borderTop: "1px solid #f0f0f0", marginTop: 10, paddingTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#595959", flexShrink: 0 }}>Variables</div>
            {detectedParams.map(param => (
              <div key={param} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 500 }}>{param}</label>
                <Input
                  value={paramValues[param] || ""}
                  onChange={e => setParamValues(prev => ({ ...prev, [param]: e.target.value }))}
                  placeholder={`Enter ${param}…`}
                  size="small"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right — bubble preview */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#8c8c8c", background: "#fafafa", borderBottom: "1px solid #f0f0f0", flexShrink: 0, letterSpacing: 0.3 }}>
          CUSTOMER'S VIEW
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 12, background: "#efeae2" }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ maxWidth: "90%", background: "#d9fdd3", borderRadius: "12px 4px 12px 12px", overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
              {imageUrl && <img src={imageUrl} alt="" style={{ width: "100%", display: "block", maxHeight: 200, objectFit: "cover" }} />}
              <div style={{ padding: "6px 10px 2px" }}>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#111", wordBreak: "break-word" }}>
                  {previewText || <span style={{ color: "#999", fontStyle: "italic" }}>No text</span>}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2, marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "#667781" }}>now ✓✓</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Query detail modal ────────────────────────────────────────────────────────
function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </div>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "10px 12px" }}>
        {children}
      </div>
    </div>
  );
}

export function DetailModal({ query, onClose }) {
  if (!query) return null;

  const createdDate =
    query?.timeline?.length > 0 && query.timeline[0]?.query_created
      ? dayjs(query.timeline[0].query_created, "YYYY-MM-DD hh:mm:ss A").format("DD-MM-YYYY hh:mm A")
      : null;

  const statusColor = () => {
    if (["Confirmed", "Confirmed & Paid", "Delivered"].includes(query.status)) return "green";
    if (["Cancelled", "Customer Declined", "Unavailable", "Query Lost", "Other"].includes(query.status)) return "red";
    if (query.status === "Waiting for Price") return "default";
    if (query.status === "Waiting for confirmation") return "gold";
    if (query.status === "Out for delivery") return "orange";
    return "blue";
  };

  const field = (label, value) =>
    value != null && value !== "" && value !== 0 ? (
      <div style={{ marginBottom: 10 }}>
        <div style={{ color: "#8c8c8c", fontSize: 11, marginBottom: 2 }}>{label}</div>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{value}</div>
      </div>
    ) : null;

  return (
    <Modal
      open={!!query}
      onCancel={onClose}
      footer={null}
      title={
        <span>
          Query{" "}
          <span style={{ color: "#1677ff" }}>#{query.query_id}</span>
          <Tag style={{ marginLeft: 12 }} color={statusColor()}>
            {query.status}
          </Tag>
        </span>
      }
      width={580}
      style={{ top: 16 }}
      styles={{ body: { height: "calc(100vh - 130px)", overflowY: "auto", paddingRight: 4 } }}
    >
      <Section label="Query Info">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {field("Query ID", query.query_id)}
          {field("Date Created", createdDate)}
          {field("User", query.user_id)}
        </div>
      </Section>

      <Section label="Vehicle">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {field("Make", query.make)}
          {field("Model", query.model)}
          {field("Body Part", query.body_part)}
          {field("Model Details", query.model_details)}
          {field("Year", query.moredetails?.year)}
          {field("Chassis No", query.chassisno)}
          {field("Cylinders", query.cylinder)}
        </div>
      </Section>

      <Section label="Customer">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {field("Name", query.customer_info?.name)}
          {field("Phone", query.phone)}
        </div>
      </Section>

      <Section label="Pricing">
        {!query.price_given && !query.best_price_received && !query.delivery_cost && !query.supplier ? (
          <div style={{ color: "#bfbfbf", fontSize: 13, fontStyle: "italic" }}>No price information available.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {field("Selling Price", query.price_given ? `${query.price_given} AED` : null)}
            {field("Best Cost Price", query.best_price_received ? `${query.best_price_received} AED` : null)}
            {field("Delivery Cost", query.delivery_cost ? `${query.delivery_cost} AED` : null)}
            {field("Supplier", query.supplier)}
          </div>
        )}
      </Section>

      {query.moredetails?.remarks && (
        <Section label="Remarks">
          <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: "#262626" }}>
            {query.moredetails.remarks}
          </div>
        </Section>
      )}

      {query.timeline?.length > 0 && (
        <Section label="Timeline">
          <Timeline
            mode="left"
            style={{ fontSize: 12, marginTop: 8 }}
            items={[...query.timeline].reverse().map(item => {
              const key = Object.keys(item)[0];
              const value = item[key];
              return { children: <span><b>{key.replace(/_/g, " ")}</b> — {value}</span> };
            })}
          />
        </Section>
      )}
    </Modal>
  );
}

export function SuppliersModal({ query, onClose }) {
  if (!query) return null;
  let suppliers = [];
  try { suppliers = JSON.parse(query.moredetails?.success || "[]"); } catch (_) { }

  const dataSource = suppliers.map((s, i) => ({
    key: i,
    no: i + 1,
    phone: (s?.phoneNumber || s || "").toString().replace("@c.us", ""),
  }));

  const columns = [
    { title: "#", dataIndex: "no", key: "no", width: 50, align: "center" },
    { title: "Phone Number", dataIndex: "phone", key: "phone" },
  ];

  return (
    <Modal
      open={!!query}
      onCancel={onClose}
      footer={null}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WhatsAppOutlined style={{ color: "#25d366" }} />
          Suppliers Reached —{" "}
          <span style={{ color: "#1677ff" }}>#{query.query_id}</span>
        </span>
      }
      width={420}
      style={{ top: 16 }}
      styles={{ body: { height: "calc(100vh - 130px)", overflowY: "auto", paddingRight: 4 } }}
    >
      {dataSource.length === 0 ? (
        <div style={{ textAlign: "center", color: "#bfbfbf", padding: "32px 0", fontSize: 13 }}>
          No suppliers were reached for this query.
        </div>
      ) : (
        <Table
          dataSource={dataSource}
          columns={columns}
          size="small"
          pagination={false}
          bordered
          style={{ borderRadius: 8, overflow: "hidden" }}
        />
      )}
    </Modal>
  );
}
